import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage(language === 'es' 
          ? 'Token de verificación inválido o faltante.' 
          : 'Invalid or missing verification token.');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/verify-email?token=${token}`, {
          withCredentials: true
        });
        
        setStatus('success');
        setMessage(response.data.message);
        
        // Set user in context (they're now logged in)
        if (response.data.user) {
          setUser(response.data.user);
        }
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/members/dashboard', { replace: true });
        }, 3000);
        
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 
          (language === 'es' 
            ? 'Error al verificar el correo electrónico. Por favor intente de nuevo.' 
            : 'Failed to verify email. Please try again.'));
      }
    };

    verifyEmail();
  }, [token, language, navigate, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center py-32 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_75f4a617-7223-46ab-b5af-9fb0ce34d58c/artifacts/s7ixbz5d_customcolor_logo_transparent_background.png" 
            alt="Exciting.Travel" 
            className="h-20 w-auto mx-auto mb-6"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#84bc00] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'es' ? 'Verificando tu correo...' : 'Verifying your email...'}
              </h1>
              <p className="text-gray-600">
                {language === 'es' ? 'Por favor espera un momento.' : 'Please wait a moment.'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-[#84bc00] mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'es' ? '¡Correo verificado!' : 'Email Verified!'}
              </h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-sm text-gray-500">
                {language === 'es' 
                  ? 'Redirigiendo al panel de miembros...' 
                  : 'Redirecting to members dashboard...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {language === 'es' ? 'Error de verificación' : 'Verification Failed'}
              </h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-4">
                <Link to="/login">
                  <Button className="w-full bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold">
                    <Mail className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Volver al inicio de sesión' : 'Back to Login'}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
